import widgetService from '@/services/widgetService';
import { announcements } from '@/data/featureAnnouncements';

const state = {
    seenAnnouncements: {},
    dismissedAnnouncements: [],
    loading: false,
    error: null,
    widgetId: null,
};

const WIDGET_TYPE = 'userPreferences';

const findUserPreferencesWidget = async (widgetService) => {
    try {
        const widgets = await widgetService.getWidgetsByType(WIDGET_TYPE);
        return widgets && widgets.length > 0 ? widgets[0] : null;
    } catch (error) {
        console.warn('Failed to fetch user preferences widget:', error);
        return null;
    }
};

const normalizeTablePreferences = (tablePreferences) => {
    if (!tablePreferences || typeof tablePreferences !== 'object') {
        return {
            tables: {},
        };
    }

    const { globalSettings, ...prefsWithoutGlobal } = tablePreferences;
    return {
        tables: Array.isArray(tablePreferences.tables) ? {} : tablePreferences.tables || {},
    };
};

const normalizeFeatureAnnouncements = (featureAnnouncements) => {
    if (!featureAnnouncements || typeof featureAnnouncements !== 'object') {
        return {
            seenAnnouncements: {},
            dismissedAnnouncements: [],
        };
    }

    const normalized = {
        seenAnnouncements: Array.isArray(featureAnnouncements.seenAnnouncements)
            ? {}
            : featureAnnouncements.seenAnnouncements || {},
        dismissedAnnouncements: Array.isArray(featureAnnouncements.dismissedAnnouncements)
            ? featureAnnouncements.dismissedAnnouncements
            : featureAnnouncements.dismissedAnnouncements || [],
    };

    return normalized;
};

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_PREFERENCES(state, { preferences, widgetId }) {
        state.seenAnnouncements = preferences.seenAnnouncements || {};
        state.dismissedAnnouncements = preferences.dismissedAnnouncements || [];
        if (widgetId) {
            state.widgetId = widgetId;
        }
    },

    MARK_AS_SEEN(state, announcementId) {
        state.seenAnnouncements[announcementId] = new Date().toISOString();
    },

    MARK_AS_DISMISSED(state, announcementId) {
        if (!state.dismissedAnnouncements.includes(announcementId)) {
            state.dismissedAnnouncements.push(announcementId);
        }
    },
};

const actions = {
    async loadPreferences({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const defaultPreferences = {
                name: 'User Preferences',
                type: WIDGET_TYPE,
                state: {
                    tablePreferences: {
                        tables: {},
                    },
                    calendarPreferences: {},
                    featureAnnouncements: {
                        seenAnnouncements: {},
                        dismissedAnnouncements: [],
                    },
                },
            };

            let widget = await findUserPreferencesWidget(widgetService);

            if (!widget) {
                widget = await widgetService.createWidget(defaultPreferences);
            }

            const rawPreferences =
                widget.state.featureAnnouncements || defaultPreferences.state.featureAnnouncements;
            const preferences = normalizeFeatureAnnouncements(rawPreferences);
            commit('SET_PREFERENCES', { preferences, widgetId: widget.uuid });

            if (
                widget &&
                (Array.isArray(widget.state.featureAnnouncements?.seenAnnouncements) ||
                    Array.isArray(widget.state.tablePreferences?.tables) ||
                    Array.isArray(widget.state.calendarPreferences))
            ) {
                const normalizedState = {
                    ...widget.state,
                    tablePreferences: normalizeTablePreferences(widget.state.tablePreferences),
                    calendarPreferences: Array.isArray(widget.state.calendarPreferences)
                        ? {}
                        : widget.state.calendarPreferences || {},
                    featureAnnouncements: preferences,
                };
                await widgetService.updateWidget(widget.uuid, { state: normalizedState });
            }

            return preferences;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async markAsSeen({ commit, state }, announcementId) {
        try {
            commit('MARK_AS_SEEN', announcementId);

            const updatedFeatureAnnouncements = {
                seenAnnouncements: state.seenAnnouncements,
                dismissedAnnouncements: state.dismissedAnnouncements,
            };

            const widget = await findUserPreferencesWidget(widgetService);
            if (widget) {
                const currentState = widget.state || {};
                await widgetService.updateWidget(widget.uuid, {
                    state: {
                        ...currentState,
                        featureAnnouncements: updatedFeatureAnnouncements,
                    },
                });
            }
        } catch (error) {
            console.warn('Failed to update seen announcements:', error);
        }
    },

    async dismissAnnouncement({ commit, state }, announcementId) {
        try {
            commit('MARK_AS_DISMISSED', announcementId);

            const updatedFeatureAnnouncements = {
                seenAnnouncements: state.seenAnnouncements,
                dismissedAnnouncements: state.dismissedAnnouncements,
            };

            const widget = await findUserPreferencesWidget(widgetService);
            if (widget) {
                const currentState = widget.state || {};
                await widgetService.updateWidget(widget.uuid, {
                    state: {
                        ...currentState,
                        featureAnnouncements: updatedFeatureAnnouncements,
                    },
                });
            }
        } catch (error) {
            console.warn('Failed to update dismissed announcements:', error);
        }
    },
};

const getters = {
    getActiveAnnouncements:
        (state) =>
        (routeName = null) => {
            const now = new Date();

            return announcements.filter((announcement) => {
                if (state.dismissedAnnouncements.includes(announcement.id)) {
                    return false;
                }

                if (announcement.startDate && new Date(announcement.startDate) > now) {
                    return false;
                }

                if (announcement.endDate && new Date(announcement.endDate) < now) {
                    return false;
                }

                if (routeName && announcement.routeNames && announcement.routeNames.length > 0) {
                    if (!announcement.routeNames.includes(routeName)) {
                        return false;
                    }
                }

                return true;
            });
        },

    hasSeenAnnouncement: (state) => (announcementId) => {
        return !!state.seenAnnouncements[announcementId];
    },

    isLoading: (state) => state.loading,
    getError: (state) => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
